import {Inject, Injectable} from '@angular/core';
import {LoadScriptsResult, Script, ScriptEntry, ScriptName, ScriptStore} from './script.model';
import {DOCUMENT} from '@angular/common';

export const scripts: Script[] = [
  {
    name: 'sldp-player',
    scriptUrl: 'https://storage.vvcr.tv/sldp-player-v2.26.0.js',
    styleUrl: null,
    type: 'text/javascript'
  },
  {
    name: 'videojs',
    scriptUrl: 'https://vjs.zencdn.net/7.10.2/video.min.js',
    styleUrl: 'https://vjs.zencdn.net/7.10.2/video-js.min.css',
    type: 'text/javascript'
  },
  {
    name: 'videojs-markers',
    scriptUrl: 'https://cdn.jsdelivr.net/npm/videojs-markers@1.0.1/dist/videojs-markers.min.js',
    styleUrl: 'https://cdn.jsdelivr.net/npm/videojs-markers@1.0.1/dist/videojs.markers.min.css',
    type: 'text/javascript'
  },
  {
    name: 'video-js-8',
    scriptUrl: 'https://vjs.zencdn.net/8.23.4/video.min.js',
    styleUrl: 'https://vjs.zencdn.net/8.23.4/video-js.css',
    type: 'text/javascript'
  },
  {
    name: 'videojs-hls-quality-selector',
    scriptUrl: 'https://cdn.jsdelivr.net/npm/videojs-hls-quality-selector@2.0.0/dist/videojs-hls-quality-selector.min.js',
    styleUrl: 'https://cdn.jsdelivr.net/npm/videojs-hls-quality-selector@2.0.0/dist/videojs-hls-quality-selector.min.css',
    type: 'text/javascript'
  },
  {
    name: 'nimio-player',
    scriptUrl: 'assets/nimio/1.14.0/nimio-v1.14.0.js',
    styleUrl: null,
    type: 'module'
  }
];

@Injectable({
  providedIn: 'root'
})
export class DynamicScriptLoaderService {
  private store: ScriptStore | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document
  ) {
    this.setStore();
  }

  public async loadScripts(names: ScriptName[]): Promise<LoadScriptsResult[]> {
    const results: LoadScriptsResult[] = [];

    for (const name of names) {
      const result: LoadScriptsResult = await this.loadScript(name);
      results.push(result);
    }

    return results;
  }

  public loadScript(name: ScriptName): Promise<LoadScriptsResult> {
    return new Promise<LoadScriptsResult>((resolve: (value: LoadScriptsResult) => void, reject: (reason: string) => void): void => {
      const script: ScriptEntry | undefined = this.store[name] || undefined;
      if (!script) {
        const message = `Script with name '${name}' not found in the script store.`;
        reject(message);
        return;
      }

      if (script.loaded === true) {
        resolve({script: name, loaded: true, status: 'Already Loaded'});
        return;
      }

      const element: HTMLScriptElement = this.document.createElement('script');
      element.type = script.type;
      element.src = script.scriptUrl;
      element.async = true;

      element.onload = (): void => {
        script.loaded = true;
        resolve({script: name, loaded: true, status: 'Loaded'});
      };

      element.onerror = (): void => {
        resolve({script: name, loaded: false, status: 'Loaded'});
      };

      if (script.styleUrl) {
        this.loadStyle(script.styleUrl);
      }

      this.document.head.appendChild(element);
    });
  }

  private setStore(): void {
    this.store = scripts
      .reduce((loaded: ScriptStore, script: Script): ScriptStore => {
        if (script.scriptUrl) {
          loaded[script.name] = {
            scriptUrl: script.scriptUrl,
            styleUrl: script.styleUrl || null,
            type: script.type,
            loaded: false
          };
        }

        return loaded;
      }, {} as ScriptStore);
  }

  private loadStyle(styleUrl: string): void {
    const element: HTMLLinkElement = this.document.createElement('link');
    element.rel = 'stylesheet';
    element.href = styleUrl;
    this.document.head.appendChild(element);
  }
}
